import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Params, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { ComponentAbstract } from '@app/components/abstract/component.abstract';
import {
  deleteNotification,
  getLastNotificationList,
  markAllAsRead,
  markAsRead,
} from '@main-application/notifications/actions/notifications.actions';
import {
  selectDeleteNotificationInProgress,
  selectLastNotificationList,
} from '@main-application/notifications/selectors/notifications.selectors';
import { AppRoutes } from '@shared/constants/app-routes.const';
import { EntityType } from '@shared/enums/entity-type';
import { NotificationCategory } from '@shared/enums/notification-category';
import { RouteData } from '@shared/enums/route-data';
import { RoutePath } from '@shared/enums/route-path.enum';
import { UserType } from '@shared/enums/user-type';
import { WebNotification } from '@shared/interfaces/notification.interface';
import { UserData } from '@shared/interfaces/user-data';
import { selectUserData } from '@shared/selectors/user.selectors';
import { ModalsService } from '@ui-components/modals/modals.service';

@UntilDestroy()
@Component({
  selector: 'app-notification-ring',
  templateUrl: './notification-ring.component.html',
  styleUrls: ['./notification-ring.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class NotificationRingComponent extends ComponentAbstract implements OnInit, OnDestroy {
  allNotificationList: WebNotification[] = [];
  displayedNotificationList: WebNotification[] = [];
  userData: UserData;
  deleteInProgress$: Observable<boolean>;
  unreadOnly = new UntypedFormControl(false);

  unreadCount = 0;

  @Input() userId: number;

  constructor(
    protected cdr: ChangeDetectorRef,
    private store: Store<{}>,
    private router: Router,
    private modalsService: ModalsService
  ) {
    super(cdr);
  }

  ngOnInit(): void {
    this.getLastMessages();

    this.deleteInProgress$ = this.store.select(selectDeleteNotificationInProgress);

    this.store
      .select(selectUserData)
      .pipe(
        untilDestroyed(this),
        filter((userData: UserData) => !!userData),
        tap((userData: UserData) => {
          this.userData = userData;
        })
      )
      .subscribe();

    this.store
      .select(selectLastNotificationList)
      .pipe(
        untilDestroyed(this),
        filter((lastNotificationList: WebNotification[]) => !!lastNotificationList),
        tap((lastNotificationList: WebNotification[]) => {
          this.allNotificationList = lastNotificationList;
          this.filterList();
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.unreadOnly.valueChanges
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.filterList();
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {}

  selectNotification(webNotification: WebNotification) {
    if (!webNotification.wasRead) {
      this.store.dispatch(
        markAsRead({
          baseNotificationBody: {
            notificationIds: [webNotification.notificationId],
          },
        })
      );
    }

    this.openSource(webNotification);
  }

  markAllAsRead() {
    this.store.dispatch(markAllAsRead());
  }

  deleteNotification(webNotification: WebNotification) {
    this.store.dispatch(
      deleteNotification({
        baseNotificationBody: {
          notificationIds: [webNotification.notificationId],
        },
      })
    );
  }

  private filterList() {
    if (this.unreadOnly.value) {
      this.displayedNotificationList = this.allNotificationList.filter(item => !item.wasRead);
    } else {
      this.displayedNotificationList = [...this.allNotificationList];
    }
    this.setUnreadCount();
    this.cdr.detectChanges();
  }

  private setUnreadCount() {
    this.unreadCount = (this.allNotificationList || []).filter(item => !item.wasRead).length;
  }

  private getLastMessages() {
    this.store.dispatch(getLastNotificationList());
  }

  private openSource(webNotification: WebNotification) {
    switch (webNotification.relatedObjectTypeId) {
      case EntityType.Ticket:
        this.modalsService.openTicketPreviewModal({
          ticketId: webNotification.relatedObjectId,
          unitName: webNotification.additionalData?.unitName,
          propertyName: webNotification.additionalData?.propertyName,
          propertyId: webNotification.additionalData?.propertyId,
        });
        break;

      case EntityType.Turnover:
      case EntityType.WorkflowStep:
        if (webNotification.additionalData?.workflowStepEnumType) {
          const params =
            webNotification.category === NotificationCategory.TurnoverComment
              ? {
                  queryParams: { [RouteData.COMMENT_ID]: webNotification.relatedObjectId },
                }
              : {};

          this.router.navigate([AppRoutes.TURNOVERS, webNotification.additionalData.turnoverIds?.[0]], params);
        }
        break;

      case EntityType.Bid:
        let queryParams: Params =
          this.userData.userType === UserType.Employee
            ? { [RouteData.SOURCE_VIEW]: RoutePath.BID }
            : { [RouteData.SOURCE_VIEW]: RoutePath.VENDOR_DASHBOARD };
        if (webNotification.category === NotificationCategory.BidComment) {
          queryParams = { ...queryParams, [RouteData.COMMENT_ID]: webNotification.relatedObjectId };
        }

        const bidId =
          webNotification.category === NotificationCategory.BidComment
            ? webNotification.additionalData.bidId
            : webNotification.relatedObjectId;

        const path: any[] =
          this.userData.userType === UserType.Employee
            ? [
                RoutePath.TURNOVERS,
                RoutePath.BID,
                webNotification.additionalData?.turnoverIds?.[0],
                RoutePath.BID_DETAIL,
                bidId,
              ]
            : [RoutePath.ROLES, RoutePath.VENDOR_DASHBOARD, RoutePath.BID_DETAIL, bidId];

        if (bidId) {
          this.router.navigate(path, {
            queryParams,
          });
        }
        break;
    }
  }
}
